import React, { useEffect, useState, useCallback } from "react";
import { Crown, Sparkles, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import GlassCard from "@/components/GlassCard";

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
}

const PLAN_DISPLAY = {
  free: "Free Trial",
  starter: "Starter",
  pro: "Pro",
  business: "Business",
};

/**
 * Reusable plan + monthly usage widget.
 * Used on Dashboard, Billing, and Autopilot — single source of truth so the
 * UI never disagrees with the backend's actual limit enforcement.
 */
export default function PlanUsageWidget({ compact = false, embedded = false, onLoaded }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/billing/subscription");
      setSub(res.data);
      onLoaded?.(res.data);
    } catch (_e) {
      // silent — the widget just renders a soft "—" state
    } finally {
      setLoading(false);
    }
  }, [onLoaded]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="text-xs text-[color:var(--text-3)] inline-flex items-center gap-2" data-testid="plan-widget-loading">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading plan…
      </div>
    );
  }
  if (!sub) return null;

  const plan = sub.effective_plan || sub.plan || "free";
  const label = PLAN_DISPLAY[plan] || plan;
  const limit = sub.monthly_limit; // null when unlimited
  const used = sub.monthly_used || 0; // backend now returns published-only count
  const scheduled = sub.monthly_scheduled || 0;
  const drafts = sub.monthly_drafts || 0;
  const isUnlimited = limit === null || limit === undefined;
  const remaining = isUnlimited ? null : Math.max(0, limit - used);
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const tone = isUnlimited
    ? "bg-[color:var(--brand-primary)]"
    : pct >= 100
    ? "bg-[color:var(--danger)]"
    : pct >= 80
    ? "bg-[color:var(--accent-warm)]"
    : "bg-[color:var(--brand-primary)]";

  const renews = sub.current_period_end ? fmtDate(sub.current_period_end) : null;
  const cancelAtPeriodEnd = !!sub.cancel_at_period_end;
  const failed = sub.last_payment_status === "failed" || sub.status === "past_due";
  const hasStripeSub = !!sub.stripe_subscription_id;
  // True briefly after checkout / billing-portal redirects, until the webhook
  // reconciles. Surfaced inline so users don't see stale plan data and
  // mistake it for a billing bug.
  const syncPending = !!sub.sync_pending;

  // ---- AI generation cap (separate from published-post cap) ----
  const aiLimit = sub.ai_generations_limit; // null = unlimited
  const aiUsed = sub.ai_generations_used || 0;
  const aiUnlimited = aiLimit === null || aiLimit === undefined;
  const aiRemaining = aiUnlimited ? null : Math.max(0, aiLimit - aiUsed);
  const aiPct = aiUnlimited ? 0 : Math.min(100, Math.round((aiUsed / Math.max(1, aiLimit)) * 100));
  const aiTone = aiUnlimited
    ? "bg-[color:var(--brand-primary)]"
    : aiPct >= 100
    ? "bg-[color:var(--danger)]"
    : aiPct >= 80
    ? "bg-[color:var(--accent-warm)]"
    : "bg-[color:var(--brand-secondary)]";

  const Wrapper = embedded ? "div" : GlassCard;
  const wrapperProps = embedded
    ? { className: "rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-4", "data-testid": "plan-usage-widget" }
    : { className: "p-4", "data-testid": "plan-usage-widget" };

  return (
    <Wrapper {...wrapperProps}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] flex items-center justify-center text-[color:var(--bg-0)] shadow-[0_4px_12px_rgba(27,168,159,0.35)] shrink-0">
            <Crown className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">Current plan</div>
            <div className="font-display text-lg font-semibold leading-tight" data-testid="plan-name">
              {label} Plan
            </div>
            <div className="text-[11px] text-[color:var(--text-3)] mt-0.5" data-testid="plan-usage-line">
              {syncPending ? (
                <span className="text-[color:var(--accent-warm)] font-medium" data-testid="plan-sync-pending">
                  Syncing subscription…
                </span>
              ) : (
                <>
                  {isUnlimited
                    ? "Unlimited published posts this month"
                    : `${used} of ${limit} published posts used this month`}
                  {renews && !cancelAtPeriodEnd ? ` · renews ${renews}` : ""}
                  {cancelAtPeriodEnd && renews ? ` · cancels ${renews}` : ""}
                </>
              )}
            </div>
          </div>
        </div>
        {!compact ? (
          <div className="flex items-center gap-2 shrink-0">
            {!isUnlimited && pct >= 100 ? (
              <Link to="/billing">
                <Button size="sm" className="bg-[color:var(--accent-warm)] hover:opacity-90 text-[color:var(--bg-0)] font-semibold" data-testid="plan-upgrade-cta">
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Upgrade
                </Button>
              </Link>
            ) : (
              <Link to="/billing">
                <Button size="sm" variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="plan-manage-cta">
                  {hasStripeSub ? <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> : null}
                  {hasStripeSub ? "Manage" : "View plans"}
                </Button>
              </Link>
            )}
          </div>
        ) : null}
      </div>

      {!isUnlimited ? (
        <div className="mt-3" data-testid="plan-meter-published">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-[color:var(--text-3)] uppercase tracking-[0.16em]">Published posts</span>
            <span className="text-[color:var(--text-2)] font-mono">{used} / {limit}</span>
          </div>
          <div className="h-2 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
            <div className={`h-full transition-all ${tone}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[11px]">
            <span className="text-[color:var(--text-3)]" data-testid="plan-remaining">
              {remaining} {remaining === 1 ? "post" : "posts"} remaining
            </span>
            <span className="text-[color:var(--text-3)] font-mono">{pct}%</span>
          </div>
          {pct >= 80 && pct < 100 ? (
            <div
              className="mt-1.5 text-[11px] text-[color:var(--accent-warm)]"
              data-testid="plan-published-near-limit"
            >
              {remaining === 1
                ? "Only 1 post remaining this month."
                : `Only ${remaining} posts remaining this month.`}{" "}
              <Link to="/billing" className="underline hover:no-underline">Upgrade for more</Link>.
            </div>
          ) : null}
        </div>
      ) : null}

      {/* AI generation meter — separate cap from published posts. Always
          shown (even on Business) since every plan has a fair-use ceiling. */}
      <div className="mt-3" data-testid="plan-meter-ai">
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="text-[color:var(--text-3)] uppercase tracking-[0.16em]">AI generations</span>
          <span className="text-[color:var(--text-2)] font-mono" data-testid="plan-ai-usage-line">
            {aiUnlimited ? `${aiUsed} used` : `${aiUsed} / ${aiLimit}`}
          </span>
        </div>
        <div className="h-2 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
          <div className={`h-full transition-all ${aiTone}`} style={{ width: `${aiPct}%` }} />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[11px]">
          <span className="text-[color:var(--text-3)]" data-testid="plan-ai-remaining">
            {aiUnlimited
              ? `${aiUsed} used · fair-use policy applies`
              : `${aiRemaining} ${aiRemaining === 1 ? "generation" : "generations"} remaining`}
          </span>
          {!aiUnlimited ? <span className="text-[color:var(--text-3)] font-mono">{aiPct}%</span> : null}
        </div>
        {!aiUnlimited && aiPct >= 80 && aiPct < 100 ? (
          <div
            className="mt-1.5 text-[11px] text-[color:var(--accent-warm)]"
            data-testid="plan-ai-near-limit"
          >
            {aiRemaining === 1
              ? "Only 1 AI generation remaining this month."
              : `Only ${aiRemaining} AI generations remaining this month.`}{" "}
            <Link to="/billing" className="underline hover:no-underline">Upgrade for more</Link>.
          </div>
        ) : null}
      </div>

      {!compact && (scheduled > 0 || drafts > 0) ? (
        <div className="mt-2 flex items-center gap-3 text-[11px] text-[color:var(--text-3)] flex-wrap" data-testid="plan-breakdown">
          {scheduled > 0 ? (
            <span data-testid="plan-breakdown-scheduled">
              <span className="text-[color:var(--text-2)] font-medium">{scheduled}</span> scheduled
            </span>
          ) : null}
          {drafts > 0 ? (
            <span data-testid="plan-breakdown-drafts">
              <span className="text-[color:var(--text-2)] font-medium">{drafts}</span> drafts
            </span>
          ) : null}
          <span className="text-[color:var(--text-3)]/70">Only successful publishes count toward your monthly limit.</span>
        </div>
      ) : null}

      {cancelAtPeriodEnd ? (
        <div className="mt-2 text-[11px] text-[color:var(--accent-warm)] inline-flex items-center gap-1.5" data-testid="plan-cancel-notice">
          <AlertTriangle className="h-3 w-3" /> Subscription set to cancel on {renews}
        </div>
      ) : null}
      {failed ? (
        <div className="mt-2 text-[11px] text-[color:var(--danger)] inline-flex items-center gap-1.5" data-testid="plan-failed-notice">
          <AlertTriangle className="h-3 w-3" /> Last payment failed — update your card in Billing
        </div>
      ) : null}
    </Wrapper>
  );
}
